import { ReactNode, useState } from 'react'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { useQuery } from '@apollo/client'
import { Box, Form } from '@factorialco/deprecated-design-system'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  AllocationFragment,
  ExpensableBudgetFragment,
  MileageRatesDocument,
  SpendingAlertFragment,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import type StepApprover from 'modules/approvals/store/models/StepApprover'
import ConfirmationScreen from 'modules/expenses/components/expenses/Detail/_Form/Common/ConfirmationScreen/mileage'
import EditExpenseFooterActions from 'modules/expenses/components/expenses/Detail/_Form/Common/EditExpenseFooterActions'
import SnackBarAlerts from 'modules/expenses/components/expenses/Detail/_Form/Common/SnackBarAlerts'
import NewMileageForm from 'modules/expenses/components/expenses/Detail/_Form/Mileage/new'
import { getMileageFormDefaultValues } from 'modules/expenses/components/expenses/Detail/_Form/Mileage/new/form/form'
import { MileageSchema } from 'modules/expenses/components/expenses/Detail/_Form/Mileage/new/form/schema'
import { useBulkActions } from 'modules/expenses/components/expenses/Detail/hooks/useBulkActions'
import { SpendingPolicesResultData } from 'modules/expenses/graphql/types'
import { useEmployeeAccess } from 'modules/expenses/hooks/useEmployeeAccess'
import {
  ExpensableMileage,
  normalizeEffectiveOn,
  useDelete,
  useSendForApproval,
  useUpdate,
  useUpdateAndApprove,
} from 'modules/expenses/hooks/useExpensable'
import { useExpenseAlerts } from 'modules/expenses/hooks/useExpenseAlerts'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { useProjectsAssignedToEmployees } from 'modules/expenses/hooks/useProjects'
import {
  isDraftExpensable,
  isManualExpensable,
  isPendingExpensable,
} from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'
import { useBudgetOptions } from 'modules/finance/hooks/useBudgets'

interface MileageFormProps {
  expensable: Expensable
  mileage: ExpensableMileage
  customizationFields: CustomizationField[]
  stepApprovers: StepApprover[]
  editable: boolean
  approvalsInformation?: ReactNode
  payrollSupplement: ReactNode
  onClose: () => void
  hasReportOnBehalfFeature: boolean
  reimburseThroughPayrollEnabled: boolean
  reimburseThroughSepaEnabled: boolean
  canCreateExpenseForOthers: boolean
  canOverrideApprovalFlow: boolean
  canSetExpensableToPaid: boolean
  canSeeEmployeeLegalEntity: boolean
  canDeleteExpensable: boolean
  onClickSnackBar: () => void
  toggleAlert: () => void
  hideAlert: boolean
  canUseSpendingPolicies: boolean
  budget?: ExpensableBudgetFragment | null
  budgetAllocation?: AllocationFragment | null
  spendingPolicyEvaluationResult?: SpendingPolicesResultData | null
  hasProjectsFeature: boolean
  registerDropHandler?: (onDrop: (files: File[]) => void) => void
  spendingAlerts?: SpendingAlertFragment[]
  hasWorkflowSpendingPolicies: boolean
  hasCostCentersFeature: boolean
}

const MileageForm = ({
  expensable,
  mileage,
  customizationFields,
  stepApprovers,
  editable,
  approvalsInformation,
  onClose,
  hasReportOnBehalfFeature,
  reimburseThroughPayrollEnabled,
  reimburseThroughSepaEnabled,
  canCreateExpenseForOthers,
  canOverrideApprovalFlow,
  canSetExpensableToPaid,
  canSeeEmployeeLegalEntity,
  canDeleteExpensable,
  onClickSnackBar,
  toggleAlert,
  canUseSpendingPolicies,
  hideAlert,
  budget,
  budgetAllocation,
  spendingPolicyEvaluationResult,
  spendingAlerts,
  hasProjectsFeature,
  hasWorkflowSpendingPolicies,
  registerDropHandler,
  hasCostCentersFeature,
}: MileageFormProps) => {
  const { employee: currentEmployee, company, access } = useCurrentSession()

  const bulkActions = useBulkActions()

  const { loading: updating } = useUpdate()
  const { loading: updatingAndApproving } = useUpdateAndApprove()
  const { loading: sendingForApproval } = useSendForApproval()
  const { loading: deleting } = useDelete()

  const isDraft = isDraftExpensable(expensable)

  const submitting = updating || updatingAndApproving || sendingForApproval || deleting

  const isDraftManual = expensable && isManualExpensable(expensable) && isDraft
  const { alertMessage, alertType } = useExpenseAlerts({
    expensableType: expensable?.type ?? 'expense',
    isDraftManual: isDraftManual ?? false,
    expensableAmount: Number(expensable.amount),
    reimbursableAmount: null, // no reimbursable amount on mileages
    budget,
    budgetAllocation,
    spendingPolicy: spendingPolicyEvaluationResult,
    mileageRoundTrip: mileage.roundTrip || false,
    mileageCalculatedMileage: Number(mileage.calculatedMileage),
    mileageMileage: Number(mileage.mileage),
    spendingAlerts,
    hasWorkflowSpendingPolicies,
  })

  const showSnackBar =
    alertMessage.length > 0 &&
    !hideAlert &&
    canUseSpendingPolicies &&
    expensable &&
    (isDraft || isPendingExpensable(expensable))

  const defaultValues = getMileageFormDefaultValues({
    employee: currentEmployee,
    customizationFields,
    groupId: expensable?.groupId,
    expensable,
  })

  const [formEmployeeId, setFormEmployeeId] = useState<number | null | undefined>(
    defaultValues.employee?.id
  )

  const { employee } = useCurrentSession()
  const employeeId = formEmployeeId ?? employee?.id

  const onEffectiveOnChange = (value: string) => {
    setFormEffectiveOn(value)
  }
  const [formEffectiveOn, setFormEffectiveOn] = useState(defaultValues.effectiveOn)

  const { projectWorkers: newProjectWorkers, loading: newLoadingProjectWorkers } =
    useProjectsAssignedToEmployees({
      skip: false,
      employeeIds: employeeId ? [employeeId] : [],
    })

  const { budgetOptions: newBudgetOptions, loading: newLoadingBudgetOptions } = useBudgetOptions({
    effectiveOn: normalizeEffectiveOn(formEffectiveOn),
    employeeId,
    skip: false,
  })

  const mileageForm = useForm({
    mode: 'onChange',
    resolver: zodResolver(
      MileageSchema({
        customizationFields,
        resourceType: 'mileage',
        budgetOptions: newBudgetOptions,
        projectWorkers: newProjectWorkers,
        hasProjectsFeature: hasProjectsFeature,
        hasCostCentersFeature: hasCostCentersFeature,
      })
    ),
    defaultValues: { ...defaultValues },
  })
  const { formState, trigger, watch } = mileageForm
  const { isValid: isValidForm, isDirty } = formState

  const [showConfirmationScreen, setShowConfirmationScreen] = useState(false)
  const [editingFromConfirmation, setEditingFromConfirmation] = useState(false)
  const [scrollTo, setScrollTo] = useState<string | undefined>(undefined)

  useUnsafeEffect(() => {
    trigger().then((validationResult) => {
      if (editingFromConfirmation) {
        setShowConfirmationScreen(false)
        return
      }
      if (validationResult) {
        setShowConfirmationScreen(true)
      }
    })
  }, [customizationFields, trigger, editingFromConfirmation])

  const { accessId: newFormAccessId } = useEmployeeAccess(formEmployeeId ?? undefined)

  const newFormCurrency = watch('currency')
  const { data: newFormRatesData } = useQuery(MileageRatesDocument, {
    variables: {
      companyId: company.id,
      accessId: newFormAccessId || access.id,
      currency: newFormCurrency,
    },
    skip: false,
  })

  const newFormRates = newFormRatesData?.expenses.rateConfigurationsConnection?.nodes ?? []

  return (
    <Form padding="s0" notRounded>
      {
        <Box gap="s32" paddingX="s24">
          <FormProvider {...mileageForm}>
            {showConfirmationScreen ? (
              <ConfirmationScreen
                expensable={expensable}
                customizationFields={customizationFields}
                resourceType="mileage"
                onEdit={(scrollToSection?: string | undefined) => {
                  setShowConfirmationScreen(false)
                  setEditingFromConfirmation(true)
                  setScrollTo(scrollToSection)
                }}
                editable={editable}
              />
            ) : (
              <NewMileageForm
                customizationFields={customizationFields}
                expensable={expensable}
                budgetOptions={newBudgetOptions}
                projectWorkers={newProjectWorkers}
                hasProjectsFeature={hasProjectsFeature}
                loadingProjectWorkers={newLoadingProjectWorkers}
                loadingBudgetOptions={newLoadingBudgetOptions}
                canEditExpensesWithPendingStatus={editable}
                registerDropHandler={registerDropHandler}
                onEmployeeIdChange={setFormEmployeeId}
                hasReportOnBehalfFeature={hasReportOnBehalfFeature}
                canCreateExpensesForOthers={canCreateExpenseForOthers}
                canSeeEmployeeLegalEntity={canSeeEmployeeLegalEntity}
                onEffectiveOnChange={onEffectiveOnChange}
                rates={newFormRates}
                scrollTo={scrollTo}
                hasCostCentersFeature={hasCostCentersFeature}
              />
            )}
          </FormProvider>
          {!submitting && !bulkActions.submitting && !isDraft && approvalsInformation}
        </Box>
      }
      <SnackBarAlerts
        showSnackBar={showSnackBar}
        alertType={alertType}
        alertMessage={alertMessage}
        toggleAlert={toggleAlert}
        onClickLink={onClickSnackBar}
      />
      <EditExpenseFooterActions
        formType={'mileage'}
        expensable={expensable}
        formValues={mileageForm.getValues()}
        customizationFields={customizationFields}
        canEdit={editable}
        canOverrideApprovalFlow={canOverrideApprovalFlow}
        canSetExpensableToPaid={canSetExpensableToPaid}
        canDeleteExpensable={canDeleteExpensable}
        reimburseThroughPayrollEnabled={reimburseThroughPayrollEnabled}
        reimburseThroughSepaEnabled={reimburseThroughSepaEnabled}
        stepApprovers={stepApprovers}
        taxAgencyOrganization={null}
        formValid={isValidForm}
        formDirty={isDirty}
        trigger={trigger}
        onClose={onClose}
      />
    </Form>
  )
}
export default observer(MileageForm)
